import { Inject, Injectable } from '@angular/core';
import { DatasourceDomain } from '@mode/shared/contract-common';
import { QueryOptions, Definition } from '@mode/shared/data-access-algolia';
import { AlgoliaSuggestionService } from './algolia-suggestion.service';
import { SuggestionService } from './suggestion.service';
import { DataIndexSuggestionService } from './data-index-suggestion.service';
import { QualityComparision, SUGGESTION_COMPARISON_TOKEN, SuggestionComparison } from './suggestion-comparison';
import { ObservabilityService, PerformanceService } from '@mode/shared/util-observability';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SearchClientService } from '@mode/algolia/domain';

interface SearchMetadata {
  datasource: DatasourceDomain.Datasource;
  searchFields: string[];
  tableName: string;
  query: string;
  indexName?: string;
}

@Injectable()
export class DualDispatchSuggestionService implements SuggestionService {
  constructor(
    private _algoliaSuggestionService: AlgoliaSuggestionService,
    private _dataIndexSuggestionService: DataIndexSuggestionService,
    @Inject(SUGGESTION_COMPARISON_TOKEN) private _comparison: SuggestionComparison,
    private _observability: ObservabilityService,
    private _performanceService: PerformanceService,
    private _searchClientService: SearchClientService
  ) {}

  getTables(
    datasource: DatasourceDomain.Datasource,
    opts: QueryOptions<any>,
    isCompletingDefinition: boolean
  ): Promise<AceAjax.Completion[]> {
    try {
      this._dataIndexSuggestionService.getTables(datasource, opts, isCompletingDefinition);
    } catch (error) {
      console.error('Call to DataIndex failed:', error);
    }
    return this._algoliaSuggestionService.getTables(datasource, opts, isCompletingDefinition);
  }

  async getColumns(
    datasource: DatasourceDomain.Datasource,
    opts: QueryOptions<'table' | 'dataSourceTable'>,
    suggestedTable: string
  ): Promise<AceAjax.Completion[]> {
    const startTime = performance.now();

    const algoliaResults = await this._algoliaSuggestionService.getColumns(datasource, opts, suggestedTable);

    try {
      const dataIndexResults = await this._dataIndexSuggestionService.getColumns(datasource, opts);

      const { algoliaResponseTime, dataIndexResponseTime } = this.getResponseTimes(startTime);

      const qualityComparision = this._comparison.compare(dataIndexResults, algoliaResults);

      this.trace(qualityComparision, {
        algoliaResponseTime: algoliaResponseTime,
        dataIndexResponseTime: dataIndexResponseTime,
        datasource: datasource,
        query: opts.query,
        searchFields: opts.attrsToSearchIn?.['table'],
        tableName: suggestedTable,
      });
    } catch (error) {
      console.error('Call to DataIndex failed:', error);
    }

    return algoliaResults;
  }

  getDefinition(opts: QueryOptions<'definition'>): Promise<Definition | null> {
    try {
      this._dataIndexSuggestionService.getDefinition(opts);
    } catch (error) {
      console.error('Call to DataIndex failed:', error);
    }
    return this._algoliaSuggestionService.getDefinition(opts);
  }

  private getResponseTimes(startTime: number): { algoliaResponseTime: any; dataIndexResponseTime: any } {
    const resourceEntries = this._performanceService.getEntriesByType('resource');
    const recentEntries = resourceEntries.filter((entry) => entry.startTime >= startTime);
    const { duration: algoliaResponseTime } = recentEntries.find(
      (entry) => entry.name.includes('algolia.net') || entry.name.includes('algolianet.com')
    );
    const { duration: dataIndexResponseTime } = recentEntries.find((entry) => entry.name.includes('/dataindex/api/'));

    return {
      algoliaResponseTime,
      dataIndexResponseTime,
    };
  }

  private trace(
    { algoliaResultCount, dataIndexResultCount, hitRate }: QualityComparision,
    {
      datasource,
      searchFields,
      tableName,
      query,
      algoliaResponseTime,
      dataIndexResponseTime,
    }: SearchMetadata & { algoliaResponseTime?: number; dataIndexResponseTime?: number }
  ) {
    const traceName = 'mode.webapp_ui.opensearch.quality';

    this._observability
      .startTrace(traceName, {
        attributes: {
          [`${traceName}.algoliaResultCount`]: algoliaResultCount,
          [`${traceName}.dataIndexResultCount`]: dataIndexResultCount,
          [`${traceName}.hitRate`]: hitRate,
          [`${traceName}.metadata.datasource`]: `${datasource.token}`,
          [`${traceName}.metadata.org`]: extractOrg(datasource.links.settings),
          [`${traceName}.metadata.searchFields`]: `${searchFields}`,
          [`${traceName}.metadata.table`]: `${tableName}`,
          [`${traceName}.metadata.indexName`]: getIndexName(datasource, this._searchClientService),
          [`${traceName}.query`]: query,
          [`${traceName}.algoliaResponseTime`]: algoliaResponseTime,
          [`${traceName}.dataIndexResponseTime`]: dataIndexResponseTime,
        },
      })
      .end();
  }
}

const extractOrg = (datasourceSettingsLink: string): string => {
  const match = datasourceSettingsLink?.match(/\/organizations\/([^\/]+)\//i);
  return match ? match[1] : 'not-found';
};

const getIndexName = (datasource: DatasourceDomain.Datasource, searchClientService: SearchClientService): string => {
  const indexedBy = datasource.public ? 'table' : 'dataSourceTable';
  return searchClientService['_indexMap'][indexedBy];
};
